import React from "react";
import SessionActivitiesTable from "./sessions/SessionActivitiesTable";
import {
  ActivityLog,
  ActivityMeasurement,
  HeartRateZone,
} from "../types/postActivity";

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const getZoneColor = (
  zoneNumber: number | undefined,
  zoneName: string | undefined,
): string => {
  switch (zoneNumber) {
    case 4:
      return "#ef4444";
    case 3:
      return "#f59e0b";
    case 2:
      return "#10b981";
    case 1:
    case 0:
      return "#3b82f6";
    default: {
      const lowerName = (zoneName || "").toLowerCase();
      if (lowerName.includes("peak") || lowerName.includes("anaerobic"))
        return "#ef4444";
      if (lowerName.includes("vigorous") || lowerName.includes("threshold"))
        return "#f59e0b";
      if (lowerName.includes("moderate") || lowerName.includes("aerobic"))
        return "#10b981";
      if (lowerName.includes("light") || lowerName.includes("easy"))
        return "#3b82f6";
      return "#6b7280";
    }
  }
};

const getZoneColorAndName = (
  zoneNumber: number | undefined,
  zoneName: string | undefined,
) => {
  switch (zoneNumber) {
    case 4:
      return { color: "#ef4444", name: "Peak" };
    case 3:
      return { color: "#f59e0b", name: "Vigorous" };
    case 2:
      return { color: "#10b981", name: "Moderate" };
    case 1:
    case 0:
      return { color: "#3b82f6", name: "Light" };
    default: {
      const lowerName = (zoneName || "").toLowerCase();
      if (lowerName.includes("peak") || lowerName.includes("anaerobic"))
        return { color: "#ef4444", name: "Peak" };
      if (lowerName.includes("vigorous") || lowerName.includes("threshold"))
        return { color: "#f59e0b", name: "Vigorous" };
      if (lowerName.includes("moderate") || lowerName.includes("aerobic"))
        return { color: "#10b981", name: "Moderate" };
      if (lowerName.includes("light") || lowerName.includes("easy"))
        return { color: "#3b82f6", name: "Light" };
      return {
        color: "#6b7280",
        name: `Zone ${zoneNumber || "Unknown"}`,
      };
    }
  }
};

export const GpsRouteSlide: React.FC<{ activityLog: ActivityLog }> = ({
  activityLog,
}) => {
  const samples = activityLog.positionData?.position_samples || [];
  const validSamples = samples.filter(
    (s) =>
      s.coords_lat_lng_deg &&
      s.coords_lat_lng_deg.length === 2 &&
      !isNaN(s.coords_lat_lng_deg[0]) &&
      !isNaN(s.coords_lat_lng_deg[1]),
  );

  const containerWidth = 400;
  const containerHeight = 200;

  let pathData = "";
  let startPoint: [number, number] | null = null;
  let endPoint: [number, number] | null = null;

  if (validSamples.length > 0) {
    const lats = validSamples.map((s) => s.coords_lat_lng_deg[0]);
    const lngs = validSamples.map((s) => s.coords_lat_lng_deg[1]);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const latRange = maxLat - minLat || 0.001;
    const lngRange = maxLng - minLng || 0.001;
    const padding = 0.1;
    const paddedMinLat = minLat - latRange * padding;
    const paddedMaxLat = maxLat + latRange * padding;
    const paddedMinLng = minLng - lngRange * padding;
    const paddedMaxLng = maxLng + lngRange * padding;

    const avgLat = (minLat + maxLat) / 2;
    const latMetersPerDegree = 111000;
    const lngMetersPerDegree = 111000 * Math.cos((avgLat * Math.PI) / 180);
    const latRangeMeters = (paddedMaxLat - paddedMinLat) * latMetersPerDegree;
    const lngRangeMeters = (paddedMaxLng - paddedMinLng) * lngMetersPerDegree;

    const gpsAspectRatio = lngRangeMeters / latRangeMeters;
    const containerAspectRatio = containerWidth / containerHeight;

    let viewBoxWidth = containerWidth;
    let viewBoxHeight = containerHeight;
    let offsetX = 0;
    let offsetY = 0;

    if (gpsAspectRatio > containerAspectRatio) {
      viewBoxHeight = containerWidth / gpsAspectRatio;
      offsetY = (containerHeight - viewBoxHeight) / 2;
    } else {
      viewBoxWidth = containerHeight * gpsAspectRatio;
      offsetX = (containerWidth - viewBoxWidth) / 2;
    }

    const gpxToSvg = (lat: number, lng: number): [number, number] => {
      const x =
        offsetX +
        ((lng - paddedMinLng) / (paddedMaxLng - paddedMinLng)) * viewBoxWidth;
      const y =
        offsetY +
        ((paddedMaxLat - lat) / (paddedMaxLat - paddedMinLat)) * viewBoxHeight;
      return [x, y];
    };

    const pathPoints = validSamples.map((sample) => {
      const [x, y] = gpxToSvg(
        sample.coords_lat_lng_deg[0],
        sample.coords_lat_lng_deg[1],
      );
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    });

    pathData =
      pathPoints.length > 0
        ? `M${pathPoints[0]} ${pathPoints
            .slice(1)
            .map((p) => `L${p}`)
            .join(" ")}`
        : "";

    startPoint = gpxToSvg(
      validSamples[0].coords_lat_lng_deg[0],
      validSamples[0].coords_lat_lng_deg[1],
    );
    if (validSamples.length > 1) {
      const last = validSamples[validSamples.length - 1];
      endPoint = gpxToSvg(
        last.coords_lat_lng_deg[0],
        last.coords_lat_lng_deg[1],
      );
    }
  }

  return (
    <div className="relative w-full h-80 md:h-96 overflow-hidden rounded-md bg-base-200">
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
        <span className="text-sm font-medium text-base-content">Route</span>
        {activityLog.provider && (
          <span className="text-xs bg-black/60 text-white px-2 py-1 rounded">
            {activityLog.provider}
          </span>
        )}
      </div>
      <div className="relative w-full h-full">
        {validSamples.length === 0 ? (
          <div className="text-base-content/60 text-center py-16">
            No valid GPS data available
          </div>
        ) : (
          <svg
            className="w-full h-full text-base-content"
            viewBox={`0 0 ${containerWidth} ${containerHeight}`}
            preserveAspectRatio="xMidYMid meet"
          >
            <rect
              width={containerWidth}
              height={containerHeight}
              fill="transparent"
            />
            {pathData && (
              <path
                d={pathData}
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {startPoint && (
              <circle
                cx={startPoint[0]}
                cy={startPoint[1]}
                r="4"
                fill="#22c55e"
                stroke="white"
                strokeWidth="2"
              />
            )}
            {endPoint && startPoint && (
              <circle
                cx={endPoint[0]}
                cy={endPoint[1]}
                r="4"
                fill="#ef4444"
                stroke="white"
                strokeWidth="2"
              />
            )}
          </svg>
        )}
      </div>
    </div>
  );
};

export const HeartRateZonesSlide: React.FC<{ activityLog: ActivityLog }> = ({
  activityLog,
}) => {
  const zones = activityLog.heartRateData?.summary?.hr_zone_data || [];
  const samples = activityLog.heartRateData?.detailed?.hr_samples || [];
  const validSamples = samples.filter((s) => s.bpm && s.timestamp);
  const avgHr = activityLog.heartRateData?.summary?.avg_hr_bpm;

  return (
    <div className="w-full h-80 md:h-96 overflow-hidden rounded-md bg-base-200 border border-base-300 p-3">
      <div className="h-full overflow-y-auto pr-1">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-medium text-base-content">
              Heart Rate Zones
            </h3>
            {activityLog.provider && (
              <span className="text-xs bg-base-100 text-base-content/80 px-2 py-1 rounded">
                {activityLog.provider}
              </span>
            )}
          </div>
          {avgHr != null && (
            <div className="text-right">
              <div className="text-xl font-bold text-base-content">
                {Math.round(avgHr)}
              </div>
              <div className="text-xs text-base-content/60">bpm (avg)</div>
            </div>
          )}
        </div>

        {validSamples.length > 0 && (
          <div className="mb-4">
            <div className="relative h-32">
              <svg
                className="w-full h-full"
                viewBox="0 0 1000 120"
                preserveAspectRatio="none"
              >
                <rect width="1000" height="120" fill="transparent" />
                {zones.map((zone: HeartRateZone, zoneIndex: number) => {
                  if (!zone.start_percentage || !zone.end_percentage)
                    return null;
                  const startY = (100 - zone.end_percentage) * 1.2;
                  const endY = (100 - zone.start_percentage) * 1.2;
                  const height = endY - startY;
                  return (
                    <rect
                      key={zoneIndex}
                      x="0"
                      y={startY}
                      width="1000"
                      height={height}
                      fill={getZoneColor(zone.zone, zone.name)}
                      opacity="0.1"
                    />
                  );
                })}
                {(() => {
                  const minHR = Math.min(...validSamples.map((s) => s.bpm!));
                  const maxHR = Math.max(...validSamples.map((s) => s.bpm!));
                  const hrRange = maxHR - minHR || 1;
                  const points = validSamples
                    .map((sample, pointIndex) => {
                      const x =
                        (pointIndex / Math.max(validSamples.length - 1, 1)) *
                        1000;
                      const y =
                        120 - ((sample.bpm! - minHR) / hrRange) * 100 - 10;
                      return `${x},${y}`;
                    })
                    .join(" ");
                  return (
                    <polyline
                      fill="none"
                      stroke="#374151"
                      strokeWidth="2"
                      points={points}
                    />
                  );
                })()}
              </svg>
            </div>
            <div className="flex justify-between text-xs text-base-content/60 mt-2">
              <span>Start</span>
              <span>End</span>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {zones
            .filter((zone) => zone.duration_seconds && zone.duration_seconds > 0)
            .sort((a, b) => (b.zone || 0) - (a.zone || 0))
            .map((zone, sortedIndex) => {
              const totalDuration =
                zones.reduce(
                  (sum, z) => sum + (z.duration_seconds || 0),
                  0,
                ) || 1;
              const percentage = Math.round(
                ((zone.duration_seconds || 0) / totalDuration) * 100,
              );
              const duration = formatDuration(
                (zone.duration_seconds || 0) / 60,
              );
              const { color, name } = getZoneColorAndName(zone.zone, zone.name);

              return (
                <div
                  key={`${zone.zone}-${sortedIndex}`}
                  className="flex items-center space-x-3"
                >
                  <div className="flex items-center space-x-2 w-24">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-sm text-base-content/80">
                      {zone.name || name}
                    </span>
                  </div>
                  <div className="flex-1 flex items-center space-x-3">
                    <div className="text-xs text-base-content/80 w-8">
                      {percentage}%
                    </div>
                    <div className="flex-1 bg-base-100 rounded-full h-2">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          backgroundColor: color,
                          width: `${percentage}%`,
                        }}
                      />
                    </div>
                    <div className="text-xs text-base-content/80 w-12">
                      {duration}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export const SessionDetailsSlide: React.FC<{
  measurements: ActivityMeasurement[];
  getActivityName: (measurement: ActivityMeasurement) => string;
}> = ({ measurements, getActivityName }) => (
  <div className="w-full h-80 md:h-96 rounded-md bg-base-200 border border-base-300 p-4 flex flex-col overflow-hidden">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-light text-base-content/60">Session Details</h3>
    </div>
    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto pr-1">
      <SessionActivitiesTable
        measurements={measurements}
        getActivityName={getActivityName}
        hideTimeColumn
      />
    </div>
  </div>
);
